/**
 * Dunes (2026-09-22): sunlit sand dunes standing one behind another, each a crest line with the
 * asymmetric profile of real sand (a long windward slope, a sharp brink, a short steep slip face)
 * closed to the bottom edge. Every dune is filled with its tile's shadow tone and only the slopes
 * that turn to the sun take a band of the lit tone fading down them, so the crest is a hard edge
 * between light and dark. All vector, no per pixel pass, so 5K costs what Full HD does.
 */
import { Rgb, Scene, WallpaperStyleInfo, darken, degrees, lighten, luminance, mix, rgba, share } from '../wallpaper-core';

export const INFO: WallpaperStyleInfo = {
  key: 'dunes', label: 'Dunes',
  blurb: 'Sunlit sand dunes with sharp crests and long shadows',
  params: [ share( 'dunes', 'Crests', 45 ), degrees( 'sun', 'Sun', 300 ) ],
};

const clamp = ( v: number, lo = 0, hi = 1 ) => Math.max( lo, Math.min( hi, v ) );

/** one slope of a dune in the box's own u (0 to 1 across it) and its height over the dune's baseline */
interface Slope {
  us: number[];
  es: number[];
  /** the brink is the first sample when true, the last when false */
  head: boolean;
}

/** one slope on the canvas: the samples left to right, the brink end, the up normal and its share of the sun */
interface Face {
  xs: number[];
  ys: number[];
  head: boolean;
  nx: number;
  ny: number;
  lit: number;
}

/**
 * The crest of one dune as a run of slopes. Brinks sit `per` apart with `wander` of jitter and one
 * of them always lands in the middle 26% of the box, so a phone crop of the middle never shows a
 * bare flank. From every brink the windward side descends over most of the gap and the leeward
 * side drops back over `slip` per unit of height, the angle of repose sand stands at: that one
 * asymmetry is what tells a dune from a wave, and the repose angle is what tells it from a fold.
 */
function crest( rand: () => number, uLo: number, uHi: number, per: number, wander: number, slip: number, density: number ): Slope[] {
  // two slow wrinkles over the windward ramp, or a straight ramp reads as a folded sheet
  const wob = [ rand() * Math.PI * 2, rand() * Math.PI * 2 ];
  const us: number[] = [ 0.37 + rand() * 0.26 ];
  while ( us[ us.length - 1 ] < uHi + per ) us.push( us[ us.length - 1 ] + per * ( 1 + wander * ( rand() - 0.5 ) ) );
  while ( us[ 0 ] > uLo - per ) us.unshift( us[ 0 ] - per * ( 1 + wander * ( rand() - 0.5 ) ) );
  // the brinks differ widely in height, or the run reads as one tooth repeated
  const hs = us.map( () => 1 - wander * rand() );
  const out: Slope[] = [];
  for ( let k = 0; k + 1 < us.length; k++ ) {
    const u0 = us[ k ], u1 = us[ k + 1 ], gap = u1 - u0;
    const low = Math.min( hs[ k ], hs[ k + 1 ] ) * ( 0.05 + 0.2 * rand() );
    // the slip face's run follows its own drop, so it stands at the same angle whatever the dune's
    // height; it may never eat more than half the gap, or the windward ramp disappears
    const ut = u1 - Math.min( 0.52 * gap, Math.max( 0.1 * gap, slip * ( hs[ k + 1 ] - low ) ) );
    // the windward face: steepest just under the brink, flattening out into the trough
    const mg = Math.max( 5, Math.round( ( ut - u0 ) * density ) );
    const gu: number[] = [], ge: number[] = [];
    for ( let s = 0; s <= mg; s++ ) {
      const t = s / mg;
      gu.push( u0 + ( ut - u0 ) * t );
      // the wrinkles are pinched out at both ends, or the ramp joins the brink and the trough on
      // a step and every dune gets a stair in its crest line
      const wrinkle = ( 0.07 * Math.sin( t * 5.1 + wob[ 0 ] ) + 0.045 * Math.sin( t * 11.3 + wob[ 1 ] ) ) * Math.sin( t * Math.PI );
      const ramp = Math.pow( 1 - t, 1.3 ) + wrinkle;
      ge.push( low + ( hs[ k ] - low ) * Math.max( 0, ramp ) );
    }
    out.push( { us: gu, es: ge, head: true } );
    // the slip face: near straight at the angle of repose, with a small apron at its foot
    const ms = Math.max( 4, Math.round( ( u1 - ut ) * density ) );
    const su: number[] = [], se: number[] = [];
    for ( let s = 0; s <= ms; s++ ) {
      const t = s / ms;
      su.push( ut + ( u1 - ut ) * t );
      se.push( low + ( hs[ k + 1 ] - low ) * Math.pow( t, 1.35 ) );
    }
    out.push( { us: su, es: se, head: false } );
  }
  return out;
}

export function draw( scene: Scene ): void {
  const { ctx, w, h, box, colors, rand, p } = scene;
  const px = box.w / 1920;
  // Screen axes, so a negative y points up: 0 stands the sun on the right at eye level, 300 high
  // on the right, 180 on the left. The light is always folded above the horizon, or half the dial
  // would be a sun under the sand with nothing lit; what the lower half changes is `carry`, how
  // far down a slope the light runs, so 90 is a hard thin edge and 270 a flooded noon.
  const sun = p[ 1 ] * Math.PI * 2;
  const sx = Math.cos( sun ), sy = Math.sin( sun );
  const up = Math.abs( sy ) * 0.85 + 0.15;
  const len = Math.hypot( sx, up ) || 1;
  const lx = sx / len, ly = -up / len;
  const elev = ( -sy + 1 ) / 2;
  // the slip faces always turn away from the light, the way a dune field lies across the wind
  const dir = sx >= 0 ? 1 : -1;

  // the tiles' order: the first tile is the sky, the rest are the dunes walked back to front
  const skyCol = colors[ 0 ];
  const tiles = colors.length > 1 ? colors.slice( 1 ) : [ lighten( skyCol, 0.4 ) ];
  const m = tiles.length;
  const n = 3 + Math.round( p[ 0 ] * 6 );
  // a pale tile has no shadow of its own, so its dark side leans on the palette's deepest colour
  const deepest = colors.reduce( ( a, c ) => luminance( c ) < luminance( a ) ? c : a );

  // the sky lives in the top third only, warming toward the sand at the horizon
  const horizon = mix( skyCol, tiles[ 0 ], 0.22 );
  const skyG = ctx.createLinearGradient( 0, 0, 0, box.y + box.h * 0.42 );
  skyG.addColorStop( 0, rgba( darken( skyCol, 0.14 ), 1 ) );
  skyG.addColorStop( 1, rgba( horizon, 1 ) );
  ctx.fillStyle = skyG;
  ctx.fillRect( 0, 0, w, h );

  // the sun's own light in the sky, on the side the slopes are lit from: no disc, since the dunes
  // are the subject and a disc would pull the eye off them
  const gx = box.x + box.w * ( 0.5 + 0.42 * sx );
  const gy = box.y + box.h * ( 0.36 - 0.3 * elev );
  const glow = ctx.createRadialGradient( gx, gy, 0, gx, gy, box.h * 0.75 );
  const glowCol = lighten( mix( skyCol, tiles[ 0 ], 0.45 ), 0.35 );
  const glowA = 0.12 + 0.16 * elev;
  // seven stops: a straight alpha ramp in a radial gradient leaves a visible cone edge
  for ( let s = 0; s <= 6; s++ ) glow.addColorStop( s / 6, rgba( glowCol, glowA * Math.pow( 1 - s / 6, 2.2 ) ) );
  ctx.globalCompositeOperation = luminance( skyCol ) < 0.55 ? 'lighter' : 'source-over';
  ctx.fillStyle = glow;
  ctx.fillRect( 0, 0, w, h );
  ctx.globalCompositeOperation = 'source-over';

  // how far the light reaches down a slope, as a share of that dune's own height: a high sun
  // floods the whole windward face, a low one leaves a bright edge under the brink and the rest
  // of the slope in shadow. It is measured per dune and not once for the field, or the near ones
  // run out of light halfway and the picture gets a dark bar along its bottom edge
  const carry = 0.35 + 0.6 * elev;
  // The field is stacked from the back: each dune is `grow` times the height of the one behind it
  // and its foot sits `drop` of that height lower, so it just covers the foot of the one behind.
  // That is the whole trick of the layout: an uncovered foot shows as a flat slab of body colour
  // and the layers stop being dunes. The first height is solved from the stack so the nearest
  // dune's own foot lands below the bottom edge, where its trough would otherwise hem the frame.
  // `spread` is how far a dune's lowest brink falls under its tallest one.
  const spread = 0.1 + 0.5 * p[ 0 ];
  const drop = 0.55;
  const grow = Math.pow( 3, 1 / Math.max( 1, n - 1 ) );
  let stack = 1;
  for ( let i = 1, g = 1; i < n; i++ ) { g *= grow; stack += drop * g; }
  const amp0 = box.h * ( 1.12 - 0.27 ) / stack;
  const castInk = darken( deepest, 0.35 );

  // the whole field is measured first, because a dune's shadow is drawn on the one behind it
  const uA = ( 0 - box.x ) / box.w - 0.03, uB = ( w - box.x ) / box.w + 0.03;
  const uLo = Math.min( uA, 1 - uB ), uHi = Math.max( uB, 1 - uA );
  const field: { faces: Face[]; lit: Rgb; shade: Rgb; top: number; foot: number; amp: number; vx: number; vy: number; cast: number }[] = [];
  let foot = 0;
  for ( let i = 0; i < n; i++ ) {
    const f = n > 1 ? i / ( n - 1 ) : 1;
    // the tiles are walked back to front: more tiles than dunes and the walk steps over some of
    // them, fewer and the last tile repeats a little darker each time, so the front is deepest
    const idx = m >= n ? ( n > 1 ? Math.round( i * ( m - 1 ) / ( n - 1 ) ) : 0 ) : Math.min( i, m - 1 );
    const base = darken( tiles[ idx ], Math.min( 0.36, 0.09 * Math.max( 0, i - ( m - 1 ) ) ) );
    // the far dunes sit back in the day's own light: without it every layer cuts against the next
    // like a sheet of coloured paper
    const base2 = mix( base, horizon, 0.26 * Math.pow( 1 - f, 1.4 ) );
    const lit = lighten( base2, 0.25 );
    const pale = clamp( ( luminance( base2 ) - 0.42 ) / 0.45 );
    let shade = mix( darken( base2, 0.38 + 0.12 * ( 1 - pale ) ), deepest, 0.45 * pale );
    // and never close enough to the lit tone to flatten the two faces into one
    const need = luminance( lit ) - 0.36;
    if ( luminance( shade ) > need ) shade = darken( shade, clamp( 1 - need / Math.max( 0.001, luminance( shade ) ), 0, 0.55 ) );
    // and a dark tile's dark side is still sand, not a hole punched in the picture
    const sunk = luminance( shade );
    if ( sunk < 0.1 ) shade = mix( shade, lit, ( 0.1 - sunk ) * 3.5 );

    // each dune is taller than the one behind and its foot sits `drop` of that height lower, so
    // it just covers that one's foot; the back dune's tallest brink stands at 27% of the box,
    // which is the sky's share of the frame, and the nearest dune's foot falls off the bottom
    const amp = amp0 * Math.pow( grow, i ) * ( 0.92 + 0.16 * rand() );
    foot = i === 0 ? box.y + box.h * 0.27 + amp : foot + drop * amp;
    const baseY = foot;
    // a dune ridge hardly ever runs level, but the lean has to stay well under the windward ramp's
    // own fall or it cancels the asymmetry and hands the light to the wrong face
    const lean = ( rand() - 0.5 ) * box.h * 0.09 * ( 0.5 + f );
    // a dune is about four times as wide as it is tall (a windward ramp some 3.2 of its drop
    // long and a slip face 1.48 of it), plus a stretch of flat corridor before the next one; tie
    // the spacing to the height or the brinks come out as peaks instead of ridges of sand
    const per = amp * 4 * ( 1.15 + 0.45 * rand() ) / box.w;
    // sand comes to rest near 34 degrees, so the slip face's run is 1.48 of its drop
    const slopes = crest( rand, uLo, uHi, per, spread, amp * 1.48 / box.w, 520 );

    const faces: Face[] = [];
    let top = h;
    for ( const s of slopes ) {
      const xs = s.us.map( u => box.x + box.w * ( dir > 0 ? u : 1 - u ) );
      const ys = s.es.map( ( e, k ) => baseY + lean * ( s.us[ k ] - 0.5 ) * 2 - amp * e );
      let head = s.head;
      // mirrored, the run comes back right to left, so it is turned round to stay one polyline
      if ( dir < 0 ) { xs.reverse(); ys.reverse(); head = !head; }
      const last = xs.length - 1;
      const cx = head ? xs[ 0 ] : xs[ last ], cy = head ? ys[ 0 ] : ys[ last ];
      const tx = ( head ? xs[ last ] : xs[ 0 ] ) - cx, ty = ( head ? ys[ last ] : ys[ 0 ] ) - cy;
      const len = Math.hypot( tx, ty ) || 1;
      let nx = ty / len, ny = -tx / len;
      if ( ny > 0 ) { nx = -nx; ny = -ny; }
      // a slope takes the sun by how square it stands to it, plus a little skylight from straight
      // above, so the face turned away is dark but never a hole
      const litAmt = clamp( 0.8 * Math.max( 0, nx * lx + ny * ly ) + 0.26 * -ny + 0.05 );
      faces.push( { xs, ys, head, nx, ny, lit: litAmt } );
      for ( const y of ys ) if ( y < top ) top = y;
    }
    if ( dir < 0 ) faces.reverse();
    const reach = amp * carry;
    field.push( { faces, lit, shade, top, foot, amp, vx: reach * sx * 0.75, vy: reach, cast: amp * ( 0.1 + 0.3 * ( 1 - elev ) ) } );
  }

  /**
   * A band along one slope: the slope itself and the same samples moved by (ox, oy), filled with a
   * gradient that runs from one edge to the other. `taper` walks the near edge out to the far one
   * over that share of each end, so the band shuts where its gradient has already died instead of
   * stopping on a straight hard line, the one mark that says folded paper instead of sand.
   */
  const ribbon = ( fc: Face, ox: number, oy: number, taper: number ) => {
    const last = fc.xs.length - 1;
    const near = ( s: number ) => {
      if ( taper <= 0 ) return 0;
      const t = s / last;
      const k = clamp( Math.min( t, 1 - t ) / taper );
      return 1 - k * k * ( 3 - 2 * k );
    };
    ctx.beginPath();
    ctx.moveTo( fc.xs[ 0 ] + ox * near( 0 ), fc.ys[ 0 ] + oy * near( 0 ) );
    for ( let s = 1; s <= last; s++ ) ctx.lineTo( fc.xs[ s ] + ox * near( s ), fc.ys[ s ] + oy * near( s ) );
    for ( let s = last; s >= 0; s-- ) ctx.lineTo( fc.xs[ s ] + ox, fc.ys[ s ] + oy );
    ctx.closePath();
    ctx.fill();
  };

  const outline = ( fs: Face[] ) => {
    ctx.beginPath();
    ctx.moveTo( fs[ 0 ].xs[ 0 ], fs[ 0 ].ys[ 0 ] );
    for ( const fc of fs ) for ( let s = 0; s < fc.xs.length; s++ ) ctx.lineTo( fc.xs[ s ], fc.ys[ s ] );
    ctx.lineTo( w + 2, h + 2 );
    ctx.lineTo( -2, h + 2 );
    ctx.closePath();
  };

  for ( let i = 0; i < n; i++ ) {
    const d = field[ i ];

    // the shadow this dune throws up onto the one behind: one ribbon per slope, laid along the
    // slope's own normal so the gradient is square to it whatever the slope does, and clipped to
    // the dune behind so it never smudges the sky
    if ( i > 0 ) {
      ctx.save();
      outline( field[ i - 1 ].faces );
      ctx.clip();
      for ( const fc of d.faces ) {
        const depth = d.cast * ( 0.35 + 0.65 * ( 1 - fc.lit ) );
        const mid = fc.xs.length >> 1;
        const g = ctx.createLinearGradient( fc.xs[ mid ], fc.ys[ mid ], fc.xs[ mid ] + fc.nx * depth, fc.ys[ mid ] + fc.ny * depth );
        g.addColorStop( 0, rgba( castInk, 0.11 ) );
        g.addColorStop( 0.45, rgba( castInk, 0.05 ) );
        g.addColorStop( 1, rgba( castInk, 0 ) );
        ctx.fillStyle = g;
        ribbon( fc, fc.nx * depth, fc.ny * depth, 0.3 );
      }
      ctx.restore();
    }

    // the whole dune in its shadow tone first
    outline( d.faces );
    // the body is the shadow tone down to the dune's foot, where the slip faces live, and lifts
    // under it: what shows there is the flat corridor to the next dune, and flat sand takes the
    // sun square. Without the lift an uncovered foot reads as a dark slab of nothing
    const yEnd = d.foot + d.amp * 0.4;
    const body = ctx.createLinearGradient( 0, d.top, 0, yEnd );
    body.addColorStop( 0, rgba( d.shade, 1 ) );
    body.addColorStop( clamp( ( d.foot - d.amp * 0.25 - d.top ) / Math.max( 1, yEnd - d.top ), 0, 0.98 ), rgba( d.shade, 1 ) );
    body.addColorStop( 1, rgba( mix( d.shade, d.lit, 0.42 ), 1 ) );
    ctx.fillStyle = body;
    ctx.fill();

    // then the lit face: a band between each sunward slope and a copy of it moved along the light,
    // its gradient anchored on that slope's own brink, so the light dies out down the slope and the
    // brink itself stays the hard edge between the two faces
    ctx.save();
    outline( d.faces );
    ctx.clip();
    for ( const fc of d.faces ) {
      if ( fc.lit < 0.04 ) continue;
      const last = fc.xs.length - 1;
      const cx = fc.head ? fc.xs[ 0 ] : fc.xs[ last ], cy = fc.head ? fc.ys[ 0 ] : fc.ys[ last ];
      const a = 0.95 * fc.lit;
      // the light runs out either at the sun's own reach or at the foot of the slope, whichever
      // comes first: past the foot the band would end on a straight edge with the tone still in it
      const ex = fc.head ? fc.xs[ last ] : fc.xs[ 0 ], ey = fc.head ? fc.ys[ last ] : fc.ys[ 0 ];
      const span = clamp( ( ( ex - cx ) * d.vx + ( ey - cy ) * d.vy ) / ( d.vx * d.vx + d.vy * d.vy ), 0.12, 1 );
      const g = ctx.createLinearGradient( cx, cy, cx + d.vx * span, cy + d.vy * span );
      g.addColorStop( 0, rgba( d.lit, a ) );
      g.addColorStop( 0.38, rgba( d.lit, a * 0.55 ) );
      g.addColorStop( 0.72, rgba( d.lit, a * 0.18 ) );
      g.addColorStop( 1, rgba( d.lit, 0 ) );
      ctx.fillStyle = g;
      ribbon( fc, d.vx, d.vy, 0 );
      const bx = d.vx * span * 0.3, by = d.vy * span * 0.3;
      const brink = ctx.createLinearGradient( cx, cy, cx + bx, cy + by );
      brink.addColorStop( 0, rgba( lighten( d.lit, 0.35 ), 0.5 * fc.lit ) );
      brink.addColorStop( 1, rgba( lighten( d.lit, 0.35 ), 0 ) );
      ctx.fillStyle = brink;
      ribbon( fc, bx, by, 0 );
    }
    ctx.restore();

    // blown sand on the crest: a hairline of the lit tone along the silhouette, brightest where
    // the slope faces the sun
    ctx.lineWidth = Math.max( 1, 2 * px );
    ctx.lineJoin = 'round';
    for ( const fc of d.faces ) {
      ctx.beginPath();
      ctx.moveTo( fc.xs[ 0 ], fc.ys[ 0 ] );
      for ( let s = 1; s < fc.xs.length; s++ ) ctx.lineTo( fc.xs[ s ], fc.ys[ s ] );
      ctx.strokeStyle = rgba( lighten( d.lit, 0.3 ), 0.05 + 0.33 * fc.lit );
      ctx.stroke();
    }
  }
}
